// ═══════════════════════════════════════════════════════════════════════
// Temps › Arrêts et certificats (agent E2) — écran de la RH habilitée (« Voir et vérifier les
// certificats médicaux ») : certificats à vérifier, certificats attendus, à compléter, en conflit.
// Une action principale par ligne : « Vérifier ». Le certificat s'ouvre par la route contrôlée
// (chaque consultation est journalisée). Le serveur refuse tout acte sur son propre arrêt.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { FileHeart, CheckCircle2, MessageSquareWarning, XCircle, Eye, Inbox, AlertTriangle } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, Onglets, formaterDate } from '../../design/composants';
import { useChargement, Fenetre, Champ, PiedFenetre, nomPersonne } from './reglagesCommun';
import { lireApi, ecrireApi, messageErreur } from './MesDemandesPage';
import { ouvrirFichierSecurise } from '../../utils/fichiersSecurises';

const ONGLETS = [
  { cle: 'a_verifier', libelle: 'À vérifier' },
  { cle: 'attendus', libelle: 'Certificats attendus' },
  { cle: 'a_completer', libelle: 'À compléter' },
  { cle: 'en_conflit', libelle: 'En conflit' },
];

function FenetreMotif({ arret, action, onFermer, onTermine }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const rejet = action === 'rejeter';
  const envoyer = async () => {
    try {
      await ecrireApi('patch', `/maladie/${arret.id}/${action}`, { motif });
      onTermine(rejet ? 'Arrêt rejeté : les journées redeviennent à qualifier.' : 'Demande de complément envoyée au salarié.');
    } catch (e) { setErreur(messageErreur(e)); }
  };
  return (
    <Fenetre titre={rejet ? 'Rejeter l\'arrêt' : 'Demander un complément'} Icone={rejet ? XCircle : MessageSquareWarning} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--sg-texte-2)' }}>
        {nomPersonne(arret.employe.prenom, arret.employe.nom)}, du {formaterDate(arret.date_debut, true)} au {formaterDate(arret.date_fin, true)}.
        {' '}Le salarié lit ce motif : n'y écrivez aucune information de santé.
      </p>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante={rejet ? 'danger' : 'primaire'} onClick={envoyer} disabled={motif.trim().length < 3}>{rejet ? 'Rejeter' : 'Envoyer'}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export default function ArretsCertificatsPage() {
  const [onglet, setOnglet] = useState('a_verifier');
  const [fenetre, setFenetre] = useState(null);
  const [message, setMessage] = useState('');
  const [erreurAction, setErreurAction] = useState('');
  const { donnees, erreur, recharger } = useChargement(() => lireApi('/maladie/suivi'), []);

  const terminer = (m) => { setFenetre(null); setMessage(m); setErreurAction(''); recharger(); };
  const agir = async (fn, m) => { try { await fn(); terminer(m); } catch (e) { setErreurAction(messageErreur(e)); } };
  const voir = async (a) => { const m = await ouvrirFichierSecurise(`/api/temps/maladie/${a.id}/certificat`); if (m) setErreurAction(m); };
  const liste = donnees ? donnees[onglet] || [] : null;
  const onglets = ONGLETS.map((o) => ({ ...o, compte: donnees ? (donnees[o.cle] || []).length : undefined }));

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Arrêts et certificats' }]} titre="Arrêts et certificats"
          sousTitre="Données de santé : accès réservé à la RH habilitée. Chaque consultation de certificat est enregistrée." />
        {(erreur || erreurAction) && <BandeauInfo ton="danger">{erreur || erreurAction}</BandeauInfo>}
        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
        <Onglets onglets={onglets} actif={onglet} onChange={setOnglet} libelle="Suivi des arrêts" />
        <Carte titre={ONGLETS.find((o) => o.cle === onglet).libelle} Icone={FileHeart}>
          {onglet === 'en_conflit' && (
            <BandeauInfo ton="alerte" titre="Arrêt déclaré pendant une autre absence validée">
              La déclaration est enregistrée, mais l'absence n'est pas reportée dans le temps. Décidez d'abord de l'autre absence par son propre circuit
              (interruption d'un congé, par exemple), puis enregistrez l'absence ici.
            </BandeauInfo>
          )}
          {!liste ? <Squelette largeur="60%" /> : liste.length === 0 ? (
            <EtatVide Icone={Inbox} titre="Rien à traiter" />
          ) : (
            <div className="rh-conteneur-tableau">
              <table className="rh-tableau">
                <thead><tr><th>Salarié</th><th>Arrêt</th><th>Certificat</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {liste.map((a) => (
                    <tr key={a.id}>
                      <td><span className="rh-cellule-principale">{nomPersonne(a.employe.prenom, a.employe.nom)}</span>
                        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{a.employe.matricule}</span></td>
                      <td>Du {formaterDate(a.date_debut, true)} au {formaterDate(a.date_fin, true)}
                        {a.en_conflit && <span style={{ display: 'block' }}><Badge ton="alerte"><AlertTriangle size={12} aria-hidden="true" /> En conflit</Badge></span>}
                        {a.motif_complement && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Complément demandé : {a.motif_complement}</span>}</td>
                      <td>
                        {a.certificat?.present
                          ? <Bouton petit variante="lien" Icone={Eye} onClick={() => voir(a)}>Voir le certificat</Bouton>
                          : <span className="rh-cellule-secondaire">{a.echeance_certificat ? `Attendu avant le ${formaterDate(a.echeance_certificat, true)}` : 'Attendu (pas d\'échéance paramétrée)'}</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {a.statut === 'CERTIFICAT_RECU' && (
                          <Bouton petit variante="primaire" Icone={CheckCircle2}
                            onClick={() => agir(() => ecrireApi('patch', `/maladie/${a.id}/verifier`, {}), 'Certificat vérifié : l\'absence est confirmée.')}>Vérifier</Bouton>
                        )}
                        {['DECLARE', 'CERTIFICAT_RECU'].includes(a.statut) && (
                          <Bouton petit variante="discret" Icone={MessageSquareWarning} onClick={() => setFenetre({ arret: a, action: 'completer' })}>Demander un complément</Bouton>
                        )}
                        {a.en_conflit && (
                          <Bouton petit variante="discret" onClick={() => agir(() => ecrireApi('patch', `/maladie/${a.id}/deposer-absence`, {}), 'Absence enregistrée dans le temps.')}>Enregistrer l'absence</Bouton>
                        )}
                        {['DECLARE', 'CERTIFICAT_RECU', 'A_COMPLETER'].includes(a.statut) && (
                          <Bouton petit variante="discret" Icone={XCircle} onClick={() => setFenetre({ arret: a, action: 'rejeter' })}>Rejeter</Bouton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
        {fenetre && <FenetreMotif arret={fenetre.arret} action={fenetre.action} onFermer={() => setFenetre(null)} onTermine={terminer} />}
      </div>
    </Layout>
  );
}
